<script>
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import AdvancedSearch from './AdvancedSearch.vue';

export default {
    components: {
        AdvancedSearch,
    },
    data () {
        return {
            view: 'map',
            location : {
                ip_lat : null,
                ip_lon : null,
                geo_lat : null,
                geo_lon : null,
                country : null,
            },
            map: null,
            geojson: {},
            markers : {},
            currentMarkers : [],
            tableViewData : {},
            search: {
                s : '',
                grade : [],
                key_awards : [],
                county : null,
                distance : null,
                award_category : null,
            },
            openModal : false,
            modalData : {},
            mapContainerStyle : {
                height : '600px',
                "min-height" : '600px'
            },
            modalContent: {
                top: 0,
                height: '300px'
            },
            locationLookupCount : 0,
            geoJsonRequestController : null,
        }
    },
    props : {
        token : {
            type: String,
            default: ''
        },
        year : {
            type: String,
            default: ''
        },
        categories : {
            type: String,
            default: ''
        },
        counties : {
            type: String,
            default: ''
        }
    },
    created() {
        /**
         * Setup the app
         */

        window.addEventListener("resize", this.resizeWindow);

        // Check if we have a stored search
        if (localStorage.getItem('blasData') !== null) {
            var data = JSON.parse(localStorage.getItem('blasData'));

            this.search = data.search;
            this.view = data.view;
        }
    },
    destroyed() {
        window.removeEventListener("resize", this.resizeWindow);
    },
    mounted() {
        this.sleep(200).then(() => {
            this.setElementHeights();
            this.buildMap();
            this.ipLookup();
        });
    },
    watch:{

    },
    computed : {
        searchBreadcrumb() {
            let breadcrumb = [];

            let award_category = this.search.award_category;
            let grades = this.search.grade;
            let key_awards = this.search.key_awards;

            if (award_category != null && award_category != 'all') {
                breadcrumb.push(this.deslugify(award_category));
            }

            if (grades.length > 0) {
                for(let i = 0; i <= grades.length - 1; i++) {
                    breadcrumb.push(this.deslugify(grades[i]));
                }
            }

            if (key_awards.length > 0) {
                for(let i = 0; i <= key_awards.length - 1; i++) {
                    breadcrumb.push(this.deslugify(key_awards[i]));
                }
            }

            return breadcrumb.join(' / ');
        }
    },
    methods: {
        getLocation() {
            /**
             * Get the location based on GeoLocation or an IP lookup fallback
             */
            let self = this;

            if ("geolocation" in navigator) {
                navigator.geolocation.getCurrentPosition(
                    function success(position) {
                        self.location.geo_lat = position.coords.latitude;
                        self.location.geo_lon = position.coords.longitude;
                        self.addMyLocation();
                        self.getGeoJson();
                    },
                    function error(error_message) {
                        self.location.geo_lat = self.location.ip_lat;
                        self.location.geo_lon = self.location.ip_lon;
                        self.addMyLocation();
                        self.getGeoJson();
                    }
                );
            } else {
                self.location.geo_lat = self.location.ip_lat;
                self.location.geo_lon = self.location.ip_lon;
                self.addMyLocation();
                self.getGeoJson();
            }
        },
        ipLookup () {
            /**
             * Complete an API lookup on the api
             */
            let self = this;

            axios.get('/api/geo', {
                'X-Requested-With' : 'XMLHttpRequest',
            })
            .then(function (response) {
                if (response.status == 200) {
                    self.location.ip_lat = response.data.lat;
                    self.location.ip_lon = response.data.lon;
                    self.location.country = response.data.country;

                    if (self.location.country != "Ireland") {
                        self.location.ip_lon = "-7.95";
                        self.location.ip_lat = "53.44";
                    }
                }
            });
        },
        buildMap () {
            /**
             * Build the map
             */
            let self = this;

            if (!this.token) {
                return;
            }

            mapboxgl.accessToken = this.token;
            this.map = new mapboxgl.Map({
                container: 'map',
                style: 'mapbox://styles/mapbox/streets-v11',
                center: [-7.9666103, 53.4232911],
                zoom: 6,
                minZoom: 6,
                maxZoom: 12
            });

            var nav = new mapboxgl.NavigationControl({showCompass:false});
            this.map.addControl(nav, 'top-right');


            this.map.on('zoomend', function() {
                self.addMarkersToMap();
            });

            this.getGeoJson();

            this.map.on('load', function() {
                self.resizeMapContainer();

                if (window.screen.width >= 640) {
                    self.map.easeTo({
                        padding: { left: 500 },
                        duration: 1000
                    });
                }
            });
        },
        getGeoJson(){
            let self = this;

            // Filter/search changes each trigger their own request with no
            // debounce-level de-duplication upstream (see AdvancedSearch's
            // 500ms debounce — two edits spaced further apart than that
            // still fire two separate requests). Without cancelling the
            // previous one, an older, slower response can arrive after a
            // newer one and silently overwrite it with stale results —
            // filters would then appear to have no effect until the page
            // is refreshed. Abort any request still in flight before
            // starting the next one.
            if (this.geoJsonRequestController) {
                this.geoJsonRequestController.abort();
            }
            this.geoJsonRequestController = new AbortController();

            let data = { ...this.search };
            data.lat = this.location.geo_lat ?? this.location.ip_lat;
            data.lon = this.location.geo_lon ?? this.location.ip_lon;
            data.year = this.year;

            this.hideModal();

            axios.post('/api/lookup', data, {
                signal: this.geoJsonRequestController.signal,
                headers: {
                    'X-Requested-With' : 'XMLHttpRequest',
                },
            })
            .then(function (response) {
                if (response.status == 200) {
                    self.geojson = JSON.parse(response.data.geojson);
                    self.buildTableViewData();
                    self.addMarkersToMap();
                }
            })
            .catch(function (error) {
                if (axios.isCancel(error)) {
                    return;
                }

                console.error(error);
            });
        },
        buildTableViewData() {
            // A filter combination can legitimately match nothing (e.g. no
            // Silver/Bronze awards yet for a year that's only had Finalists
            // assigned so far) — that must still clear out whatever the
            // previous, non-empty filter left in tableViewData, not bail
            // out and leave stale results on screen until a page refresh.
            if (!this.geojson.hasOwnProperty('features') || this.geojson.features.length == 0) {
                this.tableViewData = [];
                return;
            }

            this.tableViewData = this.geojson.features;
        },
        addMarkersToMap() {
            // Always clear existing markers first, even when the new result
            // set is empty — see the comment in buildTableViewData() above
            // for why bailing out before this would leave stale pins on the
            // map for a filter that legitimately matches nothing.
            this.removeAllMarkers();

            if (!this.geojson.hasOwnProperty('features') || this.geojson.features.length == 0) {
                return;
            }

            let self = this;
            let zoom = this.map.getZoom();
            let zoomClass = 'marker-large';

            if (zoom >=6 && zoom <= 8) {
                zoomClass = 'marker-small';
            }

            // add markers to map
            this.geojson.features.forEach(function(producer, index) {
                var el = document.createElement('div');

                el.className = 'marker-' + producer.properties.marker + ' ' + zoomClass;

                // // make a marker for each feature and add to the map
                var marker =  new mapboxgl.Marker(el)
                    .setLngLat(producer.geometry.coordinates)
                    .addTo(self.map);

                self.currentMarkers.push(marker);

                el.addEventListener('click', function(){
                    self.showModal(index);
                }, false);
            });

        },
        removeAllMarkers()
        {
            this.currentMarkers.forEach(function (marker) {
                marker.remove();
            });

            this.currentMarkers = [];
        },
        showModal(index){
            let scrollTop = document.documentElement.scrollTop;

            this.modalContent.top = this.view == 'map' || scrollTop <= 100 ? '20px' : (scrollTop - 200 )+ 'px';

            let modal = document.getElementById('company-modal');
            modal.style.height = this.modalContent.height;

            let modalContent = document.getElementById('company-modal-content');
            modalContent.style.height = this.modalContent.height;

            this.modalData = this.geojson.features[index].properties;
            this.openModal = true;
        },
        hideModal() {
            this.modalData = {};
            this.openModal = false;
        },
        setElementHeights()
        {
            /**
             * Calculates the height and width of the map to take up all available space
             */
            let appHeight = window.innerHeight;

            let header = this.$parent.$refs.header.clientHeight;
            let footer = this.$parent.$refs.footer.clientHeight;

            let height = appHeight - (header + footer);

            this.mapContainerStyle = {
                height: height + 'px',
                "min-height": height + 'px'
            }

            let modalHeight = height > 600 ? 600 : height - 20;

            this.modalContent.height = modalHeight + 'px';
        },
        resizeMapContainer() {
            let canvasContainer = document.getElementsByClassName('mapboxgl-canvas-container');
            for(let i = 0; i < canvasContainer.length; i++) {
                canvasContainer[i].style.height = this.mapContainerStyle.height;
            }
            this.map.resize();
        },
        addMyLocation()
        {
            /**
             * Adds the location circle to the map
             * If the user doesn't have the geo location set it does so
             */
            var self = this;

            if (!this.location.geo_lon || !this.location.geo_lat) {
                if (this.locationLookupCount > 3 ) {
                    return;
                }
                this.locationLookupCount++;
                this.getLocation();
                return;
            }
        },
        sleep(ms) {
            /**
             * Sleep with a promise
             */
            return new Promise(resolve => setTimeout(resolve, ms));
        },
        updateStorage()
        {
            /**
             * Save coordinates and radius to localstorage
             */
            let data = {
                'location' : this.location,
                'search' : this.search
            }

            localStorage.setItem('data', JSON.stringify(data));
        },
        deslugify(slug) {
            let words = slug.split('-');

            for (var i = 0; i < words.length; i++) {
                var word = words[i];
                words[i] = word.charAt(0).toUpperCase() + word.slice(1);
            }

            return words.join(' ');
        },
        resizeWindow() {
            this.setElementHeights();
            this.resizeMapContainer();
        },
        //emit handlers
        changeView(view) {
            this.view = view;
            this.updateStorage();
        },
        completeSearch(search) {
            this.search = search;
            this.getGeoJson();
            this.updateStorage();
        },
        sideBarToggle(isOpen) {
            let padding = isOpen ? 500 : 0;
            let width = this.$refs.index.clientWidth;

            if (isOpen) {
                this.hideModal();
            }

            if (isOpen && width > 500 ) {
                padding = 500;
            }

            if (this.map) {
                this.map.easeTo({
                    padding: { left: padding },
                    duration: 1000
                });
            }
        },
        updateStorage()
        {
            let data = {
                'view' : this.view,
                'search' : this.search
            }

            localStorage.setItem('blasData', JSON.stringify(data));
        },
    }
}
</script>


<template>
    <div class="relative" v-cloak ref="index">
        <AdvancedSearch
            :view="view"
            :search="search"
            :counties="counties"
            :categories="categories"
            :openModal="openModal"
            :mapContainer="mapContainerStyle"
            @complete-search="completeSearch"
            @change-view="changeView"
            @side-bar-toggle="sideBarToggle"
        />

        <div id="map-container" ref="mapcontainer" class="w-full h-full" v-show="view == 'map'">
            <div id="map" class="h-full w-full"></div>
        </div>
        <div id="table-view" class="h-full w-full" v-show="view == 'table'">
            <div class="px-5 ml-8 overflow-x-auto">
                <h2 class="mt-8 mb-4 text-3xl text-gold-500 font-bold">Blas na hEireann Irish Food Awards</h2>

                <div v-for="(producer, index) in tableViewData" :key="index" class="mb-7">
                    <div class="flex items-center">
                        <div class="basis-36 h-36 hidden md:flex flex-col shadow-md rounded pt-4 pb-2">
                            <div class="grow flex flex-col justify-center">
                                <h3 class="clamp text-medium px-4 overflow-hidden">{{ producer.properties.company_name }}</h3>
                            </div>

                            <div class="border border-gold-500 mx-1" ></div>

                            <div class="flex mt-2 mx-1">
                                <h4 class="text-gold-500 font-medium">Award:</h4>
                                <h3 class="ml-1 text-gold-500 font-light">
                                    {{ producer.properties.award_grades.length > 0 ? producer.properties.award_grades[0] : '' }}
                                </h3>
                            </div>
                        </div>

                        <div class="basis-auto md:basis-3/5 md:ml-8">
                            <div class="md:hidden mb-3">
                                <div class="flex mt-2">
                                    <h4 class="text-gold-500 font-medium">Award:</h4>
                                    <h3 class="ml-1 text-gold-500 font-light">
                                        {{ producer.properties.award_grades.length > 0 ? producer.properties.award_grades[0] : '' }}
                                    </h3>
                                </div>
                                <div class="border border-gold-500" ></div>
                            </div>
                            <div class="flex items-center">
                                <h4 class="text-lg font-medium text-blas-400 mr-2">Producer:</h4>
                                <h3 class="text-lg font-medium text-gold-500">{{ producer.properties.company_name }}</h3>
                            </div>

                            <div class="flex items-center mb-4">
                                <h4 class="text-lg font-medium text-blas-400 mr-2">Address:</h4>
                                <p class="text-lg font-light text-blas-400">{{ producer.properties.address }}</p>
                            </div>
                            <a :href="producer.properties.url" class="text-blas-400 underline">Read more</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-show="openModal"
            class="absolute top-0 left-0 z-30 w-full flex items-start justify-center"
            id="company-modal"
        >
            <div class="w-full sm:w-[500px] mt-4 bg-white rounded-lg shadow-lg mx-3 relative p-3 overflow-y-scroll"
                id="company-modal-content"
            >
                <div class="absolute top-0 right-0">
                    <div class="mt-4 mr-4">
                        <svg xmlns="http://www.w3.org/2000/svg"
                            class="w-2.5 h-2.5 text-gray-900 cursor-pointer stroke-current"
                            v-on:click="hideModal()"
                            viewBox="0 0 48 48">
                            <g transform="matrix(2,0,0,2,0,0)">
                                <line class="a" x1="0.75" y1="23.249" x2="23.25" y2="0.749"></line>
                                <line class="a" x1="23.25" y1="23.249" x2="0.75" y2="0.749"></line>
                            </g>
                        </svg>
                    </div>
                </div>
                <div class="mt-5 w-full">
                    <div class="flex items-center justify-center flex-col">
                        <img v-if="modalData.display_logo_url"
                            :src="modalData.display_logo_url" :alt="modalData.name" class="w-auto h-auto max-w-[211px] max-h-[65px]">

                        <h2 class="font-display text-xl text-bold text-gold-500 mt-6"
                            v-text="modalData.company_name">
                        </h2>
                    </div>

                    <div class="border border-gold-500 mb-7 mt-5" ></div>

                    <h3 class="text-xl text-gold-500 mb-3">Awards List</h3>

                    <div
                        v-for="(award, index) in modalData.awards" :key="index"
                        class="flex mb-5"
                        >
                        <span class="w-1/4 text-blas-400 font-bold">
                            {{award.award_grade}}
                        </span>
                        <span class="w-3/4 text-blas-400 font-light">
                            <span>{{ award.product_name }}</span>
                            <div>
                                <h4 class="inline text-gold-500 font-medium">Category:</h4> {{award.award_category}}
                            </div>
                        </span>
                    </div>

                    <div
                        v-for="(award, index) in modalData.special_awards" :key="index"
                        class="flex mb-5"
                        >
                        <span class="w-1/4 text-blas-400 font-bold">
                            {{award.award}}
                        </span>
                        <span class="w-3/4 text-blas-400 font-light">
                            <span>{{ modalData.company_name }}</span>
                        </span>
                    </div>
                </div>


                <div class="w-full text-center mt-10">
                    <a class="text-base text-gray-400 font-light mr-5 cursor-pointer underline"
                        v-on:click.prevent="hideModal()"
                    >
                        Close
                    </a>
                    <a :href="modalData.url"
                        class="text-base text-gray-400 font-light underline"
                    >
                        Read more
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>
